import {
  queueAccountKey,
  QueueHttpError,
  type QueueAccount,
} from "../shopify-queue/client.ts";
export type ImportAction = {
  action: "start" | "retry" | "cancel" | "schedule";
  connection_id: string;
  domain: string;
  run_id?: string;
  revision: number;
  enabled?: boolean;
  interval_minutes?: number;
};
type Saved = { id: string; body: string; apiBase?: string };
type State = {
  pending: Saved | null;
  busy: boolean;
  error: string;
  success: string;
};
type Storage = Pick<
  globalThis.Storage,
  "length" | "key" | "getItem" | "setItem" | "removeItem"
>;
export async function importRequest<T>(
  base: string,
  account: QueueAccount,
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const headers = new Headers(options.headers);
  headers.set("Content-Type", "application/json");
  headers.set("X-Shop-Domain", account.domain);
  if (account.organizationId)
    headers.set("X-Organization-Id", account.organizationId);
  const res = await fetch(
    base + "/api/sync/product-imports" + path.replace(/^\/(?=\?|$)/, ""),
    { ...options, headers, credentials: "include", cache: "no-store" },
  );
  const data = await res.json().catch(() => null);
  if (!res.ok || !data || data.success !== true)
    throw new QueueHttpError(
      data?.error || "The response could not be confirmed",
      res.ok ? 500 : res.status,
    );
  return data as T;
}
export function createImportCommands(
  base: string,
  account: QueueAccount,
  storage: Storage,
  connectionId?: string,
) {
  const prefix = "whenly-product-import-v1:" + queueAccountKey(account) + ":";
  const restore = () => {
    const keys = Array.from({ length: storage.length }, (_, i) =>
      storage.key(i),
    )
      .filter((v): v is string => !!v && v.startsWith(prefix))
      .sort();
    for (const key of keys) {
      const raw = storage.getItem(key);
      if (!raw) continue;
      const saved = JSON.parse(raw) as Saved,
        body = JSON.parse(saved.body);
      if (saved.apiBase !== undefined && saved.apiBase !== base) continue;
      // Old saved commands were written only by the production screen. Keep their exact bytes.
      if (saved.apiBase === undefined && base && base !== "https://api.whenlyapp.com") continue;
      if (connectionId !== undefined && body.connection_id !== connectionId) continue;
      if (
        key !== prefix + saved.id ||
        body.request_id !== saved.id ||
        body.expected_user_id !== account.userId
      )
        throw new Error("Saved import action is invalid");
      return saved;
    }
    return null;
  };
  let state: State = { pending: null, busy: false, error: "", success: "" };
  try {
    state.pending = restore();
  } catch {
    state.error =
      "Saved import actions could not be read. Browser storage must be available.";
  }
  const listeners = new Set<() => void>(),
    update = (values: Partial<State>) => {
      state = { ...state, ...values };
      listeners.forEach((fn) => fn());
    };
  const retry = async () => {
    if (state.busy || !state.pending) return;
    const pending = state.pending,
      command = JSON.parse(pending.body);
    update({ busy: true, error: "", success: "" });
    try {
      const result = await importRequest<{
        connection_id?: string;
        domain?: string;
        request_id: string;
        action: string;
        run_id: string | null;
        revision: number;
      }>(base, account, "/commands", { method: "POST", body: pending.body });
      if (
        (result.connection_id !== undefined && result.connection_id !== command.connection_id) ||
        (result.domain !== undefined && result.domain !== command.domain) ||
        result.request_id !== pending.id ||
        result.action !== command.action ||
        result.revision !== command.revision + 1 ||
        result.run_id !==
          (command.action === "start" ? pending.id : (command.run_id ?? null))
      )
        throw new QueueHttpError(
          "The saved receipt did not match. Retry the same action.",
          500,
        );
      storage.removeItem(prefix + pending.id);
      update({
        pending: restore(),
        success:
          command.action === "start"
            ? "Product import queued. Check its progress below."
            : command.action === "retry"
              ? "Import queued to continue from its saved progress."
              : command.action === "cancel"
                ? "Import cancelled. Completed product pages remain saved."
                : "Product import schedule saved.",
      });
    } catch (error) {
      if (
        error instanceof QueueHttpError &&
        [400, 409, 422].includes(error.status)
      ) {
        try {
          storage.removeItem(prefix + pending.id);
          update({ pending: restore() });
        } catch {
          /* Preserve uncertain local receipt. */
        }
      }
      update({
        error:
          error instanceof Error
            ? error.message
            : "The outcome is unknown. Retry the saved action.",
      });
    } finally {
      update({ busy: false });
    }
  };
  return {
    snapshot: () => state,
    subscribe: (fn: () => void) => {
      listeners.add(fn);
      return () => {
        listeners.delete(fn);
      };
    },
    retry,
    submit: async (action: ImportAction) => {
      if (state.pending || state.busy) return;
      if (connectionId !== undefined && action.connection_id !== connectionId) {
        update({ error: "Choose the store that you reviewed before saving." });
        return;
      }
      const id = crypto.randomUUID(),
        saved = {
          id,
          apiBase: base,
          body: JSON.stringify({
            ...action,
            request_id: id,
            expected_user_id: account.userId,
          }),
        };
      try {
        const previous = restore();
        if (previous) {
          update({
            pending: previous,
            error: "Confirm the saved action first.",
          });
          return;
        }
        storage.setItem(prefix + id, JSON.stringify(saved));
        if (storage.getItem(prefix + id) !== JSON.stringify(saved)) throw new Error("Browser did not retain the exact import action");
      } catch {
        update({
          error:
            "The browser could not save the action. Allow browser storage before trying again.",
        });
        return;
      }
      update({ pending: saved, error: "", success: "" });
      await retry();
    },
  };
}
